import "@tento-ui/components/input/form";
import "@tento-ui/components/icon-button";
import "@tento-ui/components/round";

import { LonaWebComponent, html } from "@tento-ui/component";
import { Row } from "@tento-ui/component-builtin";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { BrowserChrome } from "@tento-ui/components/browser-chrome";
import { Button } from "@tento-ui/components/button/button";
import { DomUtils } from "@tento-ui/dom";
import { Divider } from "@tento-ui/components/divider";
import { Form } from "@tento-ui/components/input/form";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";
import { IconButton } from "@tento-ui/components/icon-button";
import { Round } from "@tento-ui/components/round";
import { LonaModalHeader } from "@tento-ui/ui/modal-header-styles";
import { LonaIcons } from "@tento-ui/ui/icons";
import { Typography } from "@tento-ui/ui/typography";
import { Tzname } from "@tento-chrono";
import { SchedulingLinks } from "@tento-lona";
import { Actor, Patch, SchedulingLinkAttributes } from "@tento-lona/sheets";
import type { SchedulingLinkStore } from "./scheduling-link-store";

type SchedulingLinkFormInputs = Parameters<Form.Submit["onClick"]>[0];
type SchedulingLinkFormInput =
  SchedulingLinkFormInputs extends Map<string, infer T> ? T : never;
type InputWithGetValue = SchedulingLinkFormInput & { getValue: () => string };
type InputWithValue = SchedulingLinkFormInput & { value: string };

function hasGetValue(input: SchedulingLinkFormInput): input is InputWithGetValue {
  return "getValue" in input && typeof input.getValue === "function";
}

function hasStringValue(input: SchedulingLinkFormInput): input is InputWithValue {
  return "value" in input && typeof input.value === "string";
}

const DURATION_OPTIONS: Form.SelectOption[] = [
  { value: "15", label: "15 minutes" },
  { value: "30", label: "30 minutes" },
  { value: "45", label: "45 minutes" },
  { value: "60", label: "60 minutes" },
  { value: "90", label: "90 minutes" },
];

@component({
  name: "lona-create-scheduling-link-form",
})
export class CreateSchedulingLinkForm extends LonaWebComponent {
  private $bg: Round = this.hydrate<Round>("bg")
    .bind(Round.Props.MODAL);

  private store: Option<SchedulingLinkStore>;
  private link: Option<SchedulingLinks.Row>;
  private onSaved: Option<(link: SchedulingLinks.Row) => void>;
  private onDismiss: Option<() => void>;
  private onEditTimes: Option<(link: SchedulingLinks.Row) => void>;

  bind(props: CreateSchedulingLinkForm.Props): this {
    this.store = props.store;
    this.link = props.link ?? null;
    this.onSaved = props.onSaved ?? null;
    this.onDismiss = props.onDismiss ?? null;
    this.onEditTimes = props.onEditTimes ?? null;

    const isEdit = this.link != null;
    const timezone =
      this.link?.attributes.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

    this.$("heading").textContent = isEdit
      ? "Edit Scheduling Link"
      : "New Scheduling Link";
    this.$("description").textContent = isEdit
      ? "Update your scheduling link settings"
      : "Create a link others can use to book time with you";

    const builder = Form.builder()
      .textInput("title", "Title", {
        placeholder: "e.g. Coffee Chat",
        required: true,
        value: this.link?.label ?? undefined,
      })
      .textInput("slug", "URL Slug", {
        placeholder: "e.g. coffee-chat",
        required: !isEdit,
        helperText: isEdit
          ? "URL slug cannot be changed after creation"
          : "This will be used in your booking URL",
        value: this.link?.attributes.slug,
        disabled: isEdit,
      })
      .selectInput("durationMinutes", "Duration", DURATION_OPTIONS, {
        value: this.link
          ? String(this.link.attributes.durationMinutes)
          : "30",
        required: true,
      })
      .textInput("timezone", "Timezone", {
        value: timezone,
        required: true,
      })
      .richTextInput("description", "Description", {
        placeholder: "Tell your guests what this meeting is about",
        value: this.link?.attributes.description ?? undefined,
      })
      .submit({
        label: isEdit ? "Save" : "Create Link",
        onClick: ($inputs) => this.handleSubmit($inputs),
      })
      .cancel({
        label: "Cancel",
        onClick: () => this.onDismiss?.(),
      })
      .fullBleedDivider();

    const $form = builder.build().assignStyles({ width: "100%" });

    const $content = this.$("content");
    DomUtils.clearChildren($content);
    $content.appendChild($form);

    if (isEdit && this.link) {
      const captured = this.link;
      this.appendLinkActions($content, captured);
      const $editTimesBtn = Button.makeWithLight("Edit times", {
        type: "secondary",
        onClick: () => this.onEditTimes?.(captured),
      });
      $editTimesBtn.assignStyles({ width: "100%", marginTop: "8px" });
      $content.appendChild($editTimesBtn);
    }

    return this;
  }

  private appendLinkActions(
    $content: HTMLElement,
    link: SchedulingLinks.Row,
  ): void {
    const url = link.bookingUrl();
    const $panel = Row.make();
    $panel.id = "link-actions";
    $panel.assignStyles({
      alignItems: "center",
      gap: "8px",
      padding: "10px 8px",
      marginTop: "8px",
      border: "1px solid var(--divider)",
      borderRadius: "8px",
      background: "color-mix(in srgb, var(--bg) 96%, var(--gray) 4%)",
    });

    const $url = Typography.p(url, "small");
    $url.id = "public-url";

    const $copyButton = this.makeUrlButton(
      BrowserChrome.COPY_ICON,
      "Copy booking link",
      () => {
        void navigator.clipboard?.writeText(url);
      },
    );
    const $openButton = this.makeUrlButton(
      BrowserChrome.OPEN_ICON,
      "Open booking link",
      () => {
        window.open(url, "_blank", "noopener,noreferrer");
      },
    );

    $panel.appendChild($url);
    $panel.appendChild($copyButton);
    $panel.appendChild($openButton);
    $content.appendChild($panel);

    const isActive = link.attributes.isActive !== false;
    const $activeButton = Button.makeWithLight(
      isActive ? "Deactivate link" : "Reactivate link",
      {
        type: isActive ? "secondary" : "primary",
        onClick: () => {
          void this.toggleActive(link, !isActive);
        },
      },
    );
    $activeButton.assignStyles({ width: "100%", marginTop: "8px" });
    $content.appendChild($activeButton);
  }

  private makeUrlButton(
    icon: string,
    label: string,
    onClick: () => void,
  ): IconButton {
    const $button = IconButton.makeWith({
      content: LonaIcons.$svg(icon),
      sizePx: 26,
      backgroundColor: "var(--bg)",
      hoverColor: "var(--bg-hover)",
      ariaLabel: label,
    }).assignStyles({
      border: "1px solid var(--divider)",
    });
    GESTURE_MANAGER.addPointerEvent($button, { onClick });
    return $button;
  }

  private async toggleActive(
    link: SchedulingLinks.Row,
    isActive: boolean,
  ): Promise<void> {
    if (!this.store) return;
    const updated = await this.store.update(link, { isActive }, Actor.EXTERNAL);
    this.onSaved?.(updated);
  }

  private async handleSubmit($inputs: SchedulingLinkFormInputs) {
    const title = this.getInputValue($inputs, "title");
    const slug = this.getInputValue($inputs, "slug");
    const durationMinutes = Number(
      this.getInputValue($inputs, "durationMinutes"),
    );
    const timezone = Tzname.parseOpt(this.getInputValue($inputs, "timezone"));
    const description = this.getInputValue($inputs, "description") || null;

    if (this.link) {
      if (!title || !durationMinutes || !timezone) return;

      const request: SchedulingLinks.Patch = {
        title,
        durationMinutes,
        timezone,
        // An emptied field clears the description.
        description: description ?? Patch.clear,
      };

      const updated = await this.store!.update(this.link, request, Actor.EXTERNAL);
      this.onSaved?.(updated);
    } else {
      const linkSlug = SchedulingLinks.Slug.parse(slug);
      if (!title || linkSlug == null || !durationMinutes || !timezone) return;

      const request: SchedulingLinks.CreateRequest = {
        title,
        slug: linkSlug,
        durationMinutes,
        timezone,
        description,
        ownerName: null,
        availability: defaultWeekdayAvailability(),
      };

      const created = await this.store!.create(request, Actor.EXTERNAL);
      this.onSaved?.(created);
    }
  }

  private getInputValue(
    $inputs: SchedulingLinkFormInputs,
    name: string,
  ): string {
    const el = $inputs.get(name);
    if (!el) return "";
    if (hasGetValue(el)) return el.getValue();
    if (hasStringValue(el)) return el.value;
    return "";
  }

  static $styles: Style.Compat[] = [
    LonaModalHeader.$style,
    Divider.$hr,
    Button.$style,
    css`
      :host {
        display: flex;
        width: 100%;
      }

      #root {
        position: relative;
        width: 100%;
      }

      hr {
        margin: 0;
      }

      #content {
        padding: 0;
      }

      #link-actions {
        min-width: 0;
      }

      #public-url {
        min-width: 0;
        flex: 1;
        color: var(--text-sec);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      :host([inline]) #bg,
      :host([inline]) #header,
      :host([inline]) hr {
        display: none;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-col id="root">
      <std-round id="bg" floating></std-round>

      <std-col id="header" class="lona-modal-header">
        <h1 id="heading">New Scheduling Link</h1>
        <p id="description">Create a link others can use to book time with you</p>
      </std-col>
      <hr />
      <div id="content"></div>
    </std-col>
  `;
}

export namespace CreateSchedulingLinkForm {
  export interface Props {
    store: SchedulingLinkStore;
    link?: Option<SchedulingLinks.Row>;
    onSaved?: (link: SchedulingLinks.Row) => void;
    onDismiss?: () => void;
    onEditTimes?: (link: SchedulingLinks.Row) => void;
  }
}

function defaultWeekdayAvailability(): SchedulingLinkAttributes.WeeklyAvailability[] {
  const availability: SchedulingLinkAttributes.WeeklyAvailability[] = [];
  for (let day = 0; day <= 6; day++) {
    const isWeekday = day >= 1 && day <= 5;
    availability.push({
      dayOfWeek: day,
      startMinutes: 9 * 60,
      endMinutes: 17 * 60,
      isAvailable: isWeekday,
    });
  }
  return availability;
}
